import React, { useEffect, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Image, Pressable } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faExclamationCircle, faCartShopping } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { EventModel, MyFavoriteModel } from '../../types';
import { themeConfig } from '../../styles/themeConfig';
import { addFavorite, removeFavorite } from '../../store/myFavorites/myFavoritesActions';
import attachmentService from '../../services/attachmentService';
import { LoginForm } from '../../components/Auth';
import { validateLogon } from '../../utils/auth';
import { BText, BLink, Drawer } from '../';
import { setRoute } from '../../store/portal/portalActions';

type EventCardProps = {
  event: EventModel;
};

export const EventCard = ({ event }: EventCardProps) => {
  const dispatch = useAppDispatch();
  const [rtPortal, rtUser, rtMyFav] = useAppSelector(s => [s.portal, s.user, s.myFavorites]);

  const [isFav, setFav] = useState<boolean>(false);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [width, setWidth] = useState<number | string>(0);
  const [isLogon, setLogin] = useState<boolean>(false);
  const [showLogin, setShowLogin] = useState<boolean>(false);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    getImage();
    //console.log(event);
  }, []);

  useEffect(() => {
    (async () => {
      setLogin((await validateLogon(rtUser)) === true);
    })();
  }, [rtUser]);

  useEffect(() => {
    if (rtMyFav.allEvents.length && event._id) {
      let favEvent: MyFavoriteModel | undefined = rtMyFav.allEvents.find(
        (e: { eventId: string }) => e.eventId === event._id,
      );

      if (favEvent) {
        setFav(true);
      } else {
        setFav(false);
      }
    }
  }, [rtMyFav]);

  useEffect(() => {
    if (rtPortal.isSmallScreen) {
      setWidth('100%');
    } else {
      if (['md', 'lg'].includes(rtPortal.breakpoint)) {
        // let lwidth = (rtPortal.width - 2 * 16) / 2 - 8;
        let lwidth = '100%';
        setWidth(lwidth);
      } else {
        // let lwidth = (rtPortal.width - 2 * 16) / 3 - 12;
        let lwidth = '100%';
        setWidth(lwidth);
      }
    }
  }, [rtPortal.width]);

  const getImage = async () => {
    const attachment = await attachmentService.getAttachment('event', event._id);
    setThumbnail(attachment.data.files[0].image);
  };

  const toggleFav = () => {
    if (isLogon) {
      if (isFav) {
        dispatch(removeFavorite(event));
      } else {
        dispatch(addFavorite(event));
      }
    } else {
      setShowLogin(true);
    }
  };

  const showMoreBtn = () => {
    setIsExpanded(prevState => !prevState);
  };

  return (
    <View>
      <TouchableOpacity
        style={[
          isExpanded ? cardStyle.cardContainerExpanded : cardStyle.cardContainer,
          { minWidth: width, maxWidth: width },
        ]}
        onPress={() => showMoreBtn()}
      >
        {!rtPortal.isSmallScreen ? (
          <BLink to={{ screen: 'event', params: { eventId: event._id } }}>
            <View style={cardStyle.card}>
              <View style={cardStyle.cardInfo}>
                <BText style={cardStyle.eventTitle}>{event.eventName}</BText>
                <BText style={cardStyle.eventTime}>{event.displayDate}</BText>
                <BText style={cardStyle.eventVenue}>
                  {event.venueInformation.city} . {event.venueInformation.state}
                </BText>
              </View>
              {thumbnail && (
                <View style={cardStyle.eventThumbContainer}>
                  <Image source={{ uri: thumbnail }} style={cardStyle.eventThumb} />
                </View>
              )}
            </View>
          </BLink>
        ) : (
          <View style={cardStyle.card}>
            <View style={cardStyle.cardInfo}>
              <BText style={cardStyle.eventTitle}>{event.eventName}</BText>
              <BText style={cardStyle.eventTime}>{event.displayDate}</BText>
              <BText style={cardStyle.eventVenue}>
                {event.venueInformation.city} . {event.venueInformation.state}
              </BText>
            </View>
            {thumbnail && (
              <View style={cardStyle.eventThumbContainer}>
                <Image source={{ uri: thumbnail }} style={cardStyle.eventThumb} />
              </View>
            )}
          </View>
        )}
        {!rtPortal.isSmallScreen && (
          <Pressable style={cardStyle.favIconContainer} onPress={toggleFav}>
            <FontAwesomeIcon icon={faHeart} style={isFav ? cardStyle.favIconSelected : cardStyle.favIcon} />
          </Pressable>
        )}
        <Drawer id="eventcard.login" visible={showLogin} type="custom" onClose={() => setShowLogin(false)}>
          <LoginForm onClose={() => setShowLogin(false)} />
        </Drawer>
      </TouchableOpacity>
      {rtPortal.isSmallScreen && isExpanded && (
        <View style={cardStyle.expandedContainer}>
          <BLink to={{ screen: 'event', params: { eventId: event._id } }}>
            <FontAwesomeIcon icon={faExclamationCircle} style={cardStyle.eventDetailIcon} />
          </BLink>
          <View style={cardStyle.divider} />
          <TouchableOpacity onPress={() => dispatch(setRoute('checkout'))}>
            <BLink to={{ screen: 'checkout', params: { eventId: event._id } }}>
              <FontAwesomeIcon icon={faCartShopping} style={cardStyle.eventToCartIcon} />
            </BLink>
          </TouchableOpacity>
          <View style={cardStyle.divider} />
          <Pressable onPress={toggleFav}>
            <FontAwesomeIcon icon={faHeart} style={isFav ? cardStyle.favIconSelected : cardStyle.favIcon} />
          </Pressable>
        </View>
      )}
    </View>
  );
};

const cardHeight = 120; //106
const cardStyle = StyleSheet.create({
  cardContainer: {
    flex: 1,
    flexShrink: 1,
    position: 'relative',
    height: cardHeight,
    backgroundColor: themeConfig.colors.white,
    borderRadius: themeConfig.settings.borderRadius,
    ...themeConfig.settings.cardBoxShadow,
    marginBottom: 8,
    marginLeft: 8,
    marginRight: 8,
    overflow: 'hidden',
  },
  cardContainerExpanded: {
    flex: 1,
    flexShrink: 1,
    position: 'relative',
    height: cardHeight,
    backgroundColor: themeConfig.colors.white,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    ...themeConfig.settings.cardBoxShadow,
    marginBottom: 0,
    marginLeft: 8,
    marginRight: 8,
    overflow: 'hidden',
  },
  card: {
    flexDirection: 'row',
    width: '100%',
  },
  cardInfo: {
    padding: 16,
    flex: 1,
  },
  eventTitle: {
    fontSize: 16,
    fontWeight: '600',
    height: 48,
    color: themeConfig.colors.black,
  },
  eventTime: {
    fontSize: 14,
    fontWeight: '400',
    color: themeConfig.colors.grey,
  },
  eventVenue: {
    fontSize: 14,
    fontWeight: '400',
    color: themeConfig.colors.grey,
  },
  eventThumbContainer: {
    position: 'relative',
    padding: 0,
    overflow: 'hidden',
    textAlign: 'right',
  },
  eventThumb: {
    width: cardHeight,
    height: cardHeight,
  },
  eventDetailIcon: {
    color: '#fff',
  },
  eventToCartIcon: {
    color: '#12de1c',
  },
  favIconContainer: {
    position: 'absolute',
    top: 8,
    right: 8,
    borderRadius: 16,
    height: 32,
    width: 32,
    textAlign: 'center',
    zIndex: 2,
  },
  favIcon: {
    color: themeConfig.colors.white,
  },
  favIconSelected: {
    color: themeConfig.colors.red,
  },
  expandedContainer: {
    height: 30,
    position: 'relative',
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    zIndex: 999,
    backgroundColor: '#a3a3a3',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    elevation: 5,
    shadowColor: themeConfig.colors.shadow,
    shadowOffset: {
      width: -1,
      height: 3,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    marginHorizontal: 'auto',
    marginLeft: 8,
    marginRight: 8,
    marginBottom: 8,
  },
  additionalInfo: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  divider: {
    height: '100%',
    width: 1,
    backgroundColor: '#000',
  },
});
